import * as React from 'react';
import { detectScrollType } from 'normalize-scroll-left';
import { Tab } from '../Tab/Tab';
import { animateElementByProp } from '../../../common/animations';
import { TABS_DATA_HOOKS, TABS_DATA_KEYS } from '../dataHooks';
import { st, classes } from './ScrollableTabs.st.css';
import { KEYS } from '../../../common/keyCodes';
import { Component } from '../GenericComponent';
class ScrollableTabs extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedIndicatorRect: {
                left: 0,
                width: 0,
            },
            focusedTabIdx: this.props.activeTabIndex,
        };
        this._tabRefs = {};
        this._scrollToViewIfNeeded = () => {
            const { rtl, scrollButtons, activeTabIndex } = this.props;
            const { left, right } = scrollButtons;
            const selectedTabRef = this._getSelectedTabRef(activeTabIndex);
            if (selectedTabRef) {
                const { offsetWidth: tabOffsetWidth, offsetLeft: tabOffsetLeft } = selectedTabRef;
                const navElement = this._navRef.current;
                const navOffsetWidth = navElement.offsetWidth;
                const navScrollWidth = navElement.scrollWidth;
                const buttonGap = rtl ? right : left;
                let scrollTo = tabOffsetLeft - buttonGap;
                if (rtl) {
                    const rtlOffset = navOffsetWidth - tabOffsetWidth - 2 * buttonGap;
                    switch (this._rtlScrollType) {
                        case 'default':
                            scrollTo += navScrollWidth - navOffsetWidth - rtlOffset;
                            break;
                        case 'reverse':
                            scrollTo = -scrollTo + rtlOffset;
                            break;
                        default:
                            scrollTo -= rtlOffset;
                    }
                }
                this._animateScroll(scrollTo);
            }
        };
        this._getDataAttributes = () => ({
            [TABS_DATA_KEYS.alignment]: this.props.alignment,
            [TABS_DATA_KEYS.variant]: this.props.variant,
        });
        this._handleKeyDown = (e) => {
            const lastTabIdx = Object.keys(this._tabRefs).length - 1;
            const { focusedTabIdx } = this.state;
            switch (e.key) {
                case KEYS.ArrowLeft:
                case KEYS.ArrowRight: {
                    let tabIdx;
                    e.preventDefault();
                    if (e.key === KEYS.ArrowLeft) {
                        tabIdx = focusedTabIdx - 1 < 0 ? lastTabIdx : focusedTabIdx - 1;
                    }
                    else {
                        tabIdx = focusedTabIdx + 1 > lastTabIdx ? 0 : focusedTabIdx + 1;
                    }
                    return this._setTabFocus(tabIdx);
                }
                default:
                    return;
            }
        };
        this._setTabFocus = (idx) => this.setState({ focusedTabIdx: idx }, () => this._tabRefs[idx].focus());
        this._getSelectedTabRef = (idx) => this._tabRefs[idx];
        this._navRef = React.createRef();
        this._listRef = React.createRef();
    }
    componentDidMount() {
        const { rtl } = this.props;
        if (rtl) {
            this._setRtlScrollType();
        }
        this._updateComponent(true);
    }
    isSameItem(item1, item2) {
        return (item1.href === item2.href &&
            item1.id === item2.id &&
            item1.prefix === item2.prefix &&
            item1.rel === item2.rel &&
            item1.target === item2.target &&
            item1.title === item2.title);
    }
    didItemsChange(items1, items2) {
        if ((items1 === null || items1 === void 0 ? void 0 : items1.length) !== (items2 === null || items2 === void 0 ? void 0 : items2.length)) {
            return true;
        }
        const items2Hash = items2.reduce((acc, crr) => {
            return Object.assign(Object.assign({}, acc), { [crr.id]: crr });
        }, {});
        for (const item1 of items1) {
            const item2 = items2Hash[item1.id];
            if (!item2 || !this.isSameItem(item1, item2)) {
                return true;
            }
        }
        return false;
    }
    componentDidUpdate(prevProps) {
        if (this.props.rtl && !this._rtlScrollType) {
            this._setRtlScrollType();
        }
        if (prevProps.activeTabIndex !== this.props.activeTabIndex ||
            this.didItemsChange(prevProps.items, this.props.items) ||
            prevProps.alignment !== this.props.alignment ||
            prevProps.variant !== this.props.variant ||
            prevProps.rtl !== this.props.rtl) {
            this._updateComponent(prevProps.activeTabIndex !== this.props.activeTabIndex ||
                prevProps.rtl !== this.props.rtl);
        }
    }
    updateIndicatorPosition() {
        this._updateIndicatorPosition();
    }
    _setRtlScrollType() {
        this._rtlScrollType = detectScrollType();
    }
    _getNormalizedScrollLeft() {
        const { rtl } = this.props;
        let normalizedLeft = 0;
        if (this._navRef.current) {
            const { scrollLeft, scrollWidth, offsetWidth } = this._navRef.current;
            normalizedLeft = Math.abs(scrollLeft);
            if (rtl &&
                (this._rtlScrollType === 'negative' ||
                    this._rtlScrollType === 'reverse')) {
                normalizedLeft = scrollWidth - (normalizedLeft + offsetWidth);
            }
        }
        return normalizedLeft;
    }
    _getNormalizedScrollRight(scrollLeft) {
        let normalizedRight = 0;
        if (this._navRef.current) {
            const { scrollWidth, offsetWidth } = this._navRef.current;
            normalizedRight = scrollWidth - (scrollLeft + offsetWidth);
        }
        return normalizedRight;
    }
    _updateComponent(shouldScroll = false) {
        this._updateIndicatorPosition();
        if (shouldScroll) {
            this._scrollToViewIfNeeded();
        }
    }
    _updateIndicatorPosition() {
        const { selectedIndicatorRect } = this.state;
        const selectedTabRef = this._getSelectedTabRef(this.props.activeTabIndex);
        if (selectedTabRef) {
            const { offsetWidth: newWidth, offsetLeft: newLeft } = selectedTabRef;
            if (selectedIndicatorRect.left !== newLeft ||
                selectedIndicatorRect.width !== newWidth) {
                this.setState({
                    selectedIndicatorRect: { left: newLeft, width: newWidth },
                });
            }
        }
    }
    _getMaxMinScroll() {
        const { rtl } = this.props;
        let min = 0;
        let max = 0;
        if (this._navRef.current) {
            const { scrollWidth, offsetWidth } = this._navRef.current;
            max = scrollWidth - offsetWidth;
            if (rtl) {
                /* eslint-disable-next-line */
                switch (this._rtlScrollType) {
                    case 'negative':
                        min = -1 * max;
                        max = 0;
                }
            }
        }
        return {
            min,
            max,
        };
    }
    _animateScroll(scrollTo) {
        if (this._cancelAnimation) {
            this._cancelAnimation();
        }
        const { min, max } = this._getMaxMinScroll();
        const moveTo = Math.min(Math.max(scrollTo, min), max);
        const { cancel, done } = animateElementByProp({
            propToAnimate: 'scrollLeft',
            element: this._navRef.current,
            moveTo,
            duration: 400,
        });
        this._cancelAnimation = cancel;
        done
            .then(() => {
            this._cancelAnimation = null;
        })
            .catch(() => { });
    }
    scrollLeft() {
        this._scrollToSide(-1);
    }
    scrollRight() {
        this._scrollToSide(1);
    }
    _scrollToSide(scrollDirection) {
        if (this._navRef.current) {
            const { scrollLeft, clientWidth } = this._navRef.current;
            const rtlDirectionFix = this._rtlScrollType === 'reverse' ? -1 : 1;
            const scrollDistance = rtlDirectionFix * scrollDirection * (clientWidth / 2);
            this._animateScroll(scrollLeft + scrollDistance);
        }
    }
    getNavScrollPosition() {
        const scrollPosition = { scrollLeft: 0, scrollRight: 0 };
        if (this._navRef && this._navRef.current) {
            const scrollLeft = this._getNormalizedScrollLeft();
            scrollPosition.scrollLeft = scrollLeft;
            scrollPosition.scrollRight = this._getNormalizedScrollRight(scrollLeft);
        }
        return scrollPosition;
    }
    _addRef(ref, index) {
        if (ref) {
            this._tabRefs[index] = ref;
        }
    }
    render() {
        const { selectedIndicatorRect } = this.state;
        const { activeTabIndex, onClickItem, onScroll, items, alignment, variant, animateIndicator, className, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabeledBy, 'aria-describedby': ariaDescribedBy, isTablist, } = this.props;
        return (React.createElement("div", Object.assign({ className: st(classes.root, { alignment, variant, animateIndicator }, className) }, this._getDataAttributes(), { "data-hook": TABS_DATA_HOOKS.scrollableTabs }),
            React.createElement(Component, { className: classes.nav, ref: this._navRef, onScroll: onScroll, "aria-labelledby": isTablist ? null : ariaLabeledBy, "aria-label": isTablist ? null : ariaLabel, "aria-describedby": isTablist ? null : ariaDescribedBy, renderAs: isTablist ? 'div' : 'nav' },
                React.createElement(Component, Object.assign({ className: classes.itemsList, ref: this._listRef, role: isTablist ? 'tablist' : undefined }, (isTablist
                    ? {
                        renderAs: 'div',
                        onKeyDown: this._handleKeyDown,
                        'aria-label': ariaLabel,
                        'aria-labelledby': ariaLabeledBy,
                        'aria-describedby': ariaDescribedBy,
                    }
                    : { renderAs: 'ul' })), items.map((item, index) => (React.createElement(Tab, { key: `${item.title}-${index}`, className: classes.tab, item: item, index: index, "data-hook": `${TABS_DATA_HOOKS.tab}-${index}`, isActive: activeTabIndex === index, indicateActive: !animateIndicator, ref: (ref) => this._addRef(ref, index), onClick: (idx) => {
                        if (isTablist) {
                            this.setState({ focusedTabIdx: idx });
                        }
                        onClickItem(idx);
                    }, isTabDiv: isTablist })))),
                React.createElement("div", { className: classes.selectedIndicator, style: selectedIndicatorRect })),
            React.createElement("div", { className: classes.border })));
    }
}
ScrollableTabs.defaultProps = {
    scrollButtons: {
        left: 0,
        right: 0,
    },
    isTablist: false,
};
export { ScrollableTabs };
//# sourceMappingURL=ScrollableTabs.js.map